import React, { useState } from 'react'
import { Modal, Form, Input, Button, Upload, message, Row, Col, Divider, Typography, Space, Tooltip } from 'antd'
import {
	UploadOutlined,
	CameraOutlined,
	GlobalOutlined,
	PictureOutlined,
	BankOutlined,
	InfoCircleOutlined,
	EnvironmentOutlined,
	RadiusSettingOutlined,
	PlusOutlined,
	EditOutlined,
} from '@ant-design/icons'
import type { UploadFile } from 'antd/es/upload/interface'
import { httpPatch } from '../../common/http.service'
import { refreshBranding, useBranding } from '../../common/branding/useBranding'
import { useThemeColors } from '../../common/ThemeContext'

const { Text, Title } = Typography

interface OrganizationProfileUpdateProps {
	isOpen: boolean
	mode: 'create' | 'edit'
	orgInfo: any
	onClose: () => void
	onSuccess: () => void
	onSubmit: (values: any) => Promise<void>
	loading: boolean
}

function OrganizationProfileUpdate({
	isOpen,
	mode,
	orgInfo,
	onClose,
	onSuccess,
	onSubmit,
	loading,
}: OrganizationProfileUpdateProps) {
	const { primary } = useThemeColors()
	const { brandingLogoUrl } = useBranding()
	const [form] = Form.useForm()
	const [photoFileList, setPhotoFileList] = useState<UploadFile[]>([])
	const [photoUploading, setPhotoUploading] = useState(false)
	const [logoFileList, setLogoFileList] = useState<UploadFile[]>([])
	const [logoUploading, setLogoUploading] = useState(false)

	// Initialize form when modal opens
	React.useEffect(() => {
		if (isOpen && mode === 'edit') {
			form.setFieldsValue({
				organizationName: orgInfo.organizationName,
				description: orgInfo.description,
				longitude: orgInfo.longitude,
				latitude: orgInfo.latitude,
				allowCheckInDistance: orgInfo.allowCheckInDistance,
				websiteUrl: orgInfo.websiteUrl ?? '',
			})
			// Set photo preview if exists
			if (orgInfo.photoUrl) {
				setPhotoFileList([
					{
						uid: '-1',
						name: 'organization-photo.jpg',
						status: 'done',
						url: orgInfo.photoUrl,
					},
				])
			}
		} else if (isOpen && mode === 'create') {
			form.resetFields()
			setPhotoFileList([])
		}
	}, [isOpen, mode, orgInfo, form])

	const handleClose = () => {
		form.resetFields()
		setPhotoFileList([])
		setLogoFileList([])
		onClose()
	}

	const handlePhotoChange = (info: any) => {
		let newFileList = [...info.fileList]
		newFileList = newFileList.slice(-1)
		setPhotoFileList(newFileList)
	}

	const handlePhotoUpload = async () => {
		if (mode !== 'edit') {
			message.warning('Please save the organization first before uploading photo')
			return
		}

		if (photoFileList.length === 0 || !photoFileList[0].originFileObj) {
			message.warning('Please select a photo to upload')
			return
		}

		try {
			setPhotoUploading(true)
			const formData = new FormData()
			formData.append('photo', photoFileList[0].originFileObj)
			await httpPatch('/api/v1/admin/org/photo', formData, {
				'Content-Type': 'multipart/form-data',
			})
			message.success('Organization photo uploaded successfully')
			onSuccess()
		} catch (err) {
			console.error('Upload failed', err)
			message.error('Failed to upload organization photo')
		} finally {
			setPhotoUploading(false)
		}
	}

	const handleLogoChange = (info: any) => {
		setLogoFileList([...info.fileList].slice(-1))
	}

	const handleLogoUpload = async () => {
		if (mode !== 'edit') {
			message.warning('Please save the organization first before uploading the logo')
			return
		}
		if (logoFileList.length === 0 || !logoFileList[0].originFileObj) {
			message.warning('Please select a logo to upload')
			return
		}

		try {
			setLogoUploading(true)
			const formData = new FormData()
			formData.append('logo', logoFileList[0].originFileObj as File)
			await httpPatch('/api/v1/admin/app-settings/branding/logo', formData, {
				'Content-Type': 'multipart/form-data',
			})
			await refreshBranding()
			setLogoFileList([])
			message.success('Branding logo uploaded successfully')
		} catch (err: any) {
			console.error('Logo upload failed', err)
			message.error(err?.response?.data?.message || 'Failed to upload branding logo')
		} finally {
			setLogoUploading(false)
		}
	}

	const beforeUpload = (file: File) => {
		const allowed = ['image/jpeg', 'image/png', 'image/webp']
		const isValidType = allowed.includes(file.type)
		if (!isValidType) {
			message.error('Only JPEG, PNG, and WebP formats are allowed!')
			return Upload.LIST_IGNORE
		}
		const isValidSize = file.size <= 5 * 1024 * 1024
		if (!isValidSize) {
			message.error('Photo must be smaller than 5MB!')
			return Upload.LIST_IGNORE
		}
		return false // Prevent auto upload
	}

	const logoPreviewSrc = logoFileList[0]?.originFileObj
		? URL.createObjectURL(logoFileList[0].originFileObj as File)
		: brandingLogoUrl

	return (
		<Modal
			title={
				<Space>
					{mode === 'create' ? <PlusOutlined /> : <EditOutlined />}
					<span>{mode === 'create' ? 'Setup Organization' : 'Edit Organization'}</span>
				</Space>
			}
			open={isOpen}
			onCancel={handleClose}
			footer={null}
			width={800}
			destroyOnHidden
			centered
		>
			<Form
				form={form}
				layout='vertical'
				onFinish={onSubmit}
				requiredMark="optional"
			>
				<Divider orientation="left">General</Divider>
				<Row gutter={16}>
					<Col span={24}>
						<Form.Item
							label='Name'
							name='organizationName'
							rules={[{ required: true, message: 'Please enter name' }]}
						>
							<Input prefix={<BankOutlined style={{ color: '#bfbfbf' }} />} placeholder='e.g. Electricité du Cambodge' size='large' />
						</Form.Item>
					</Col>
					<Col span={24}>
						<Form.Item
							label='Description'
							name='description'
							rules={[{ required: true, message: 'Please enter description' }]}
						>
							<Input.TextArea rows={4} placeholder='Organization description...' size='large' />
						</Form.Item>
					</Col>
					<Col span={24}>
						<Form.Item
							label='Website'
							name='websiteUrl'
							rules={[
								{
									validator: (_, value) => {
										if (!value || String(value).trim().length === 0) {
											return Promise.resolve()
										}
										try {
											const parsed = new URL(String(value))
											if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
												return Promise.reject(new Error('URL must start with http:// or https://'))
											}
											return Promise.resolve()
										} catch {
											return Promise.reject(new Error('Please enter a valid URL'))
										}
									},
								},
							]}
						>
							<Input placeholder='https://www.example.com' size='large' allowClear />
						</Form.Item>
					</Col>
				</Row>

				<Divider orientation="left">Location</Divider>
				<Row gutter={16}>
					<Col span={12}>
						<Form.Item
							label='Latitude'
							name='latitude'
							rules={[
								{ required: true, message: 'Required' },
								{ pattern: /^-?([0-9]{1,2}\.?[0-9]*)$/, message: 'Invalid' },
							]}
						>
							<Input placeholder='11.5621' type='number' step='any' size='large' />
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							label='Longitude'
							name='longitude'
							rules={[
								{ required: true, message: 'Required' },
								{ pattern: /^-?([0-9]{1,3}\.?[0-9]*)$/, message: 'Invalid' },
							]}
						>
							<Input placeholder='104.9202' type='number' step='any' size='large' />
						</Form.Item>
					</Col>
					<Col span={24}>
						<Form.Item
							label={
								<Space>
									<span>Radius</span>
									<Tooltip title="Maximum distance (meters) for check-in.">
										<InfoCircleOutlined style={{ color: '#1890ff' }} />
									</Tooltip>
								</Space>
							}
							name='allowCheckInDistance'
							rules={[
								{ required: true, message: 'Please set radius' },
								{ pattern: /^[0-9]+$/, message: 'Must be a positive number' },
							]}
						>
							<Input
								prefix={<RadiusSettingOutlined style={{ color: '#bfbfbf' }} />}
								suffix="meters"
								placeholder='200'
								type='number'
								size='large'
							/>
						</Form.Item>
					</Col>
				</Row>

				{mode === 'edit' && (
					<>
						<Divider orientation="left">Branding</Divider>
						<Row gutter={24}>
							<Col xs={24} md={12}>
								<Form.Item label="Logo">
									<div style={{ padding: 16, border: '1px solid #f0f0f0', borderRadius: 8, background: '#fafafa' }}>
										<div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12 }}>
											<div style={{ width: 64, height: 64, border: '1px solid #ddd', borderRadius: 8, overflow: 'hidden', background: '#fff', display: 'flex', alignItems: 'center', justifyItems: 'center' }}>
												{logoPreviewSrc ? <img src={logoPreviewSrc} style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 4 }} alt="Logo" /> : <PictureOutlined style={{ fontSize: 24, margin: 'auto' }} />}
											</div>
											<Upload
												showUploadList={false}
												beforeUpload={beforeUpload}
												onChange={handleLogoChange}
												fileList={logoFileList}
											>
												<Button icon={<UploadOutlined />}>Select</Button>
											</Upload>
										</div>
										{logoFileList.length > 0 && (
											<Button block type="primary" onClick={handleLogoUpload} loading={logoUploading} size="small">
												Upload
											</Button>
										)}
									</div>
								</Form.Item>
							</Col>
							<Col xs={24} md={12}>
								<Form.Item label="Banner">
									<div style={{ padding: 16, border: '1px solid #f0f0f0', borderRadius: 8, background: '#fafafa' }}>
										<div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12 }}>
											<div style={{ width: 64, height: 64, border: '1px solid #ddd', borderRadius: 8, overflow: 'hidden', background: '#fff', display: 'flex', alignItems: 'center', justifyItems: 'center' }}>
												{orgInfo.photoUrl ? <img src={orgInfo.photoUrl} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="Banner" /> : <CameraOutlined style={{ fontSize: 24, margin: 'auto' }} />}
											</div>
											<Upload
												showUploadList={false}
												beforeUpload={beforeUpload}
												onChange={handlePhotoChange}
												fileList={photoFileList}
											>
												<Button icon={<UploadOutlined />}>Select</Button>
											</Upload>
										</div>
										{photoFileList.length > 0 && photoFileList[0].originFileObj && (
											<Button block type="primary" onClick={handlePhotoUpload} loading={photoUploading} size="small">
												Upload
											</Button>
										)}
									</div>
								</Form.Item>
							</Col>
						</Row>
					</>
				)}

				<div style={{ marginTop: 32, textAlign: 'right' }}>
					<Space>
						<Button onClick={handleClose} size='large'>
							Cancel
						</Button>
						<Button
							type='primary'
							htmlType='submit'
							loading={loading}
							size='large'
							style={{ minWidth: 120, background: primary, borderColor: primary }}
						>
							{mode === 'create' ? 'Create Organization' : 'Save Changes'}
						</Button>
					</Space>
				</div>
			</Form>
		</Modal>
	)
}

export default OrganizationProfileUpdate

